import React, { useState } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import axios from "axios";
import Swal from "sweetalert2";
import config from "../../config";

function BulkSettings() {
    const [bulkLoading, setBulkLoading] = useState(false);

    // Discount state
    const [bulkStartOrder, setBulkStartOrder] = useState("");
    const [bulkEndOrder, setBulkEndOrder] = useState("");
    const [bulkDiscount, setBulkDiscount] = useState("");

    // Price adjustment state
    const [bulkPriceStartOrder, setBulkPriceStartOrder] = useState("");
    const [bulkPriceEndOrder, setBulkPriceEndOrder] = useState("");
    const [bulkPriceAdjustmentValue, setBulkPriceAdjustmentValue] = useState("");
    const [bulkPriceAdjustmentType, setBulkPriceAdjustmentType] = useState("fixed");

    const applyBulkDiscount = async () => {
        if (!bulkStartOrder || !bulkEndOrder || !bulkDiscount) {
            Swal.fire("Error", "Fill all fields", "error");
            return;
        }

        try {
            setBulkLoading(true);
            const res = await axios.put(`${config.API_URL}/posts/bulk/discount`, {
                startOrder: Number(bulkStartOrder),
                endOrder: Number(bulkEndOrder),
                discount: Number(bulkDiscount),
            });

            Swal.fire("Success", `Updated ${res.data.modified} posts`, "success");
            setBulkStartOrder("");
            setBulkEndOrder("");
            setBulkDiscount("");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Bulk update failed", "error");
        } finally {
            setBulkLoading(false);
        }
    };

    const applyBulkPriceAdjustment = async (operation) => {
        if (!bulkPriceStartOrder || !bulkPriceEndOrder || !bulkPriceAdjustmentValue) {
            Swal.fire("Error", "Fill all fields", "error");
            return;
        }

        try {
            setBulkLoading(true);
            const res = await axios.put(`${config.API_URL}/posts/bulk/price-adjust`, {
                startOrder: Number(bulkPriceStartOrder),
                endOrder: Number(bulkPriceEndOrder),
                adjustmentValue: Number(bulkPriceAdjustmentValue),
                adjustmentType: bulkPriceAdjustmentType,
                operation: operation,
            });

            Swal.fire("Success", `Updated ${res.data.modified} posts`, "success");
            setBulkPriceStartOrder("");
            setBulkPriceEndOrder("");
            setBulkPriceAdjustmentValue("");
        } catch (err) {
            console.error("Bulk price update error:", err.response?.data || err.message);
            Swal.fire("Error", `Bulk price update failed: ${err.response?.data?.message || err.message}`, "error");
        } finally {
            setBulkLoading(false);
        }
    };

    const Content = (
        <div className="p-4 space-y-8">
            <div>
                <h1 className="text-3xl font-bold text-white mb-2">Bulk Management</h1>
                <p className="text-gray-400">Apply global changes to multiple mods at once.</p>
            </div>

            {/* 🔥 BULK DISCOUNT PANEL */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl transition-all hover:border-green-500/50">
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                    <span className="w-2 h-6 bg-green-500 rounded-full mr-3"></span>
                    Discount Panel — Global Setter
                </h2>

                <div className="flex flex-wrap items-end gap-6">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Start Index</label>
                        <input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={bulkStartOrder}
                            onChange={(e) => setBulkStartOrder(e.target.value)}
                            className="w-32 px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-all"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">End Index</label>
                        <input
                            type="number"
                            min="0"
                            placeholder="100"
                            value={bulkEndOrder}
                            onChange={(e) => setBulkEndOrder(e.target.value)}
                            className="w-32 px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-all"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Discount %</label>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            placeholder="20"
                            value={bulkDiscount}
                            onChange={(e) => setBulkDiscount(e.target.value)}
                            className="w-32 px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-all"
                        />
                    </div>

                    <button
                        onClick={applyBulkDiscount}
                        disabled={bulkLoading}
                        className="h-11 px-8 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold shadow-lg shadow-green-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                    >
                        {bulkLoading ? "Processing..." : "Apply Discount"}
                    </button>
                </div>
            </div>

            {/* 🔥 PRICE ADJUSTMENT PANEL */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl transition-all hover:border-indigo-500/50">
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                    <span className="w-2 h-6 bg-indigo-500 rounded-full mr-3"></span>
                    Price Adjustment — Global Setter
                </h2>

                <div className="flex flex-wrap items-end gap-6">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Start Index</label>
                        <input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={bulkPriceStartOrder}
                            onChange={(e) => setBulkPriceStartOrder(e.target.value)}
                            className="w-32 px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">End Index</label>
                        <input
                            type="number"
                            min="0"
                            placeholder="100"
                            value={bulkPriceEndOrder}
                            onChange={(e) => setBulkPriceEndOrder(e.target.value)}
                            className="w-32 px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Value ($ or %)</label>
                        <input
                            type="number"
                            min="0"
                            placeholder="5"
                            value={bulkPriceAdjustmentValue}
                            onChange={(e) => setBulkPriceAdjustmentValue(e.target.value)}
                            className="w-32 px-4 py-2 rounded-lg bg-gray-900 text-white border border-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Type</label>
                        <select
                            value={bulkPriceAdjustmentType}
                            onChange={(e) => setBulkPriceAdjustmentType(e.target.value)}
                            className="h-11 px-4 rounded-lg bg-gray-900 text-white border border-gray-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all cursor-pointer"
                        >
                            <option value="fixed">Fixed ($)</option>
                            <option value="percentage">Percentage (%)</option>
                        </select>
                    </div>

                    <div className="flex gap-4">
                        <button
                            onClick={() => applyBulkPriceAdjustment("increase")}
                            disabled={bulkLoading}
                            className="h-11 px-6 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-lg shadow-indigo-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                        >
                            {bulkLoading ? "..." : "Increase"}
                        </button>

                        <button
                            onClick={() => applyBulkPriceAdjustment("decrease")}
                            disabled={bulkLoading}
                            className="h-11 px-6 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg shadow-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                        >
                            {bulkLoading ? "..." : "Decrease"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return <AdminLayout Content={Content} />;
}

export default BulkSettings;
